import { useState, useEffect } from 'react';
import { getCommonCountryDataApi } from 'app/services/holidayApi';

// Global cache to share data across all instances of the hook
let cachedCountries: Array<{ label: string; value: string }> | null = null;
let isFetching = false;
let fetchPromise: Promise<void> | null = null;

export interface Country {
  label: string;
  value: string;
}

/**
 * Custom hook to fetch and cache country data from API.
 * Uses global cache so multiple components can share the same data without redundant API calls.
 *
 * @returns {Object} - { countries: Country[], isLoading: boolean }
 */
export const useCountries = () => {
  const [countries, setCountries] = useState<Country[]>(cachedCountries || []);
  const [isLoading, setIsLoading] = useState(!cachedCountries);

  useEffect(() => {
    // If already cached, use it immediately
    if (cachedCountries) {
      setCountries(cachedCountries);
      setIsLoading(false);
      return;
    }

    // If another instance is already fetching, wait for it
    if (isFetching && fetchPromise) {
      fetchPromise.then(() => {
        if (cachedCountries) {
          setCountries(cachedCountries);
          setIsLoading(false);
        }
      });
      return;
    }

    // Start fetching
    isFetching = true;
    setIsLoading(true);

    fetchPromise = getCommonCountryDataApi({})
      .then((response: any) => {
        const data = response?.data || response;
        // Try different possible response structures
        const rawCountries = Array.isArray(data)
          ? data
          : data?.countries || data?.data || [];
        
        if (Array.isArray(rawCountries) && rawCountries.length > 0) {
          const formatted = rawCountries.map((c: any) => ({
            label:
              c.name ||
              c.label ||
              c.title ||
              String(c.code || '').replace(/_/g, ' '),
            value: c.code || c.value || c.id || c.name,
          }));
          
          // Cache the result globally
          cachedCountries = formatted;
          setCountries(formatted);
        } else {
          // Fallback to minimal list if API returns empty or invalid data
          cachedCountries = [
            { label: 'Việt Nam', value: 'VN' },
            { label: 'Hoa Kỳ', value: 'US' },
            { label: 'Anh', value: 'GB' },
          ];
          setCountries(cachedCountries);
        }
      })
      .catch(error => {
        console.error('[useCountries] Failed to fetch countries:', error);
        // Use fallback on error
        cachedCountries = [
          { label: 'Việt Nam', value: 'VN' },
          { label: 'Hoa Kỳ', value: 'US' },
          { label: 'Anh', value: 'GB' },
        ];
        setCountries(cachedCountries);
      })
      .finally(() => {
        isFetching = false;
        setIsLoading(false);
        fetchPromise = null;
      });
  }, []);

  return { countries, isLoading };
};

/**
 * Clear the cached countries data (useful for testing or forcing refresh)
 */
export const clearCountriesCache = () => {
  cachedCountries = null;
  isFetching = false;
  fetchPromise = null;
};
